import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { environment } from '@environments/environments';
import { Permission } from '@shared/enums/permission.enum';
import { Role } from '@shared/enums/roles.enum';
import { SalesRepSubjectType } from '@shared/enums/sales-rep-assignment.enum';
import { CreateUserPayload, UserCreditCardRow, UsersListRequest } from '@shared/models/domain/custom-views.model';
import { AdminUserView, ProjectView, SubscriptionView } from '@shared/models/domain/domain-view.model';
import { AspNetRole, AspNetUser, AspNetUserRole, Tag, UserCreditCard, UserProfile, UserTag } from '@shared/models/domain/domain.model';
import { BulkRequest } from '@shared/models/request/bulk-request.model';
import { PageRequest } from '@shared/models/request/page-request.model';
import { PageResponse } from '@shared/models/response/page-response.model';
import { SubscriptionDetailView } from '@shared/models/views/subscription-view.model';
import { UserAccountView } from '@shared/models/views/user-account-view.model';
import { SalesRepAssignmentViewService } from '@shared/services/admin/sales-rep-assignment.service';
import { CoreApiService } from '@shared/services/common/core-api.service';
import { ReadOnlyCoreApiService } from '@shared/services/common/read-only-core-api.service.';
import { CardTypeLogos } from '@shared/services/credit-card.service';
import { BehaviorSubject, filter, forkJoin, ignoreElements, map, merge, mergeMap, Observable, of, switchMap } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AdminUsersService {
    private _httpClient = inject(HttpClient);
    private authService = inject(AuthService);
    private salesRepAssignmentViewService = inject(SalesRepAssignmentViewService);
    private apiUrl: string = environment.apiUrl;
    private usersViewService = new ReadOnlyCoreApiService<AdminUserView>('admin/view/users');
    private _users = new BehaviorSubject<PageResponse<AdminUserView> | null>(null);
    private _user = new BehaviorSubject<UserAccountView | null>(null);
    private _userSubscription = new BehaviorSubject<SubscriptionDetailView | null>(null);
    private _projectSubscription = new BehaviorSubject<PageResponse<SubscriptionView> | null>(null);
    private _userCards = new BehaviorSubject<PageResponse<UserCreditCard> | null>(null);
    get users$(): Observable<PageResponse<AdminUserView>> {
        return this._users.asObservable();
    }

    get user$(): Observable<UserAccountView> {
        return this._user.asObservable();
    }

    get userSubscription$(): Observable<SubscriptionDetailView> {
        return this._userSubscription.asObservable();
    }

    get projectSubscription$(): Observable<PageResponse<SubscriptionView>> {
        return this._projectSubscription.asObservable();
    }

    get userCards$(): Observable<PageResponse<UserCreditCard>> {
        return this._userCards.asObservable();
    }

    getUser(userId: string): Observable<UserAccountView> {
        userId = !userId ? this.authService?.userProfile?.userProfile?.userId : userId;
        return this._httpClient.get<PageResponse<UserAccountView>>(this.apiUrl + 'api/admin/view/users/account/' + userId).pipe(
            map((data) => {
                const ret = data.item;
                this._user.next(ret);
                return ret;
            })
        );
    }

    createUser(user: CreateUserPayload, noObservableEmit = false): Observable<UserAccountView> {
        const salesRepUserId = typeof user?.salesRepresentativeUserId === 'string' && user.salesRepresentativeUserId !== 'none' ? user.salesRepresentativeUserId : null;
        return this._httpClient.post<PageResponse<UserAccountView>>(this.apiUrl + 'api/admin/user/register', user).pipe(
            map((data) => {
                const ret = data.item;
                if (!noObservableEmit) this._user.next(ret);
                return ret;
            }),
            mergeMap((ret) => salesRepUserId && ret?.user?.id
                ? merge(of(ret), this.salesRepAssignmentViewService.checkSentForApproval(SalesRepSubjectType.UserProfile, ret.user.id, salesRepUserId).pipe(ignoreElements()))
                : of(ret))
        );
    }

    updateUser(user: AspNetUser, profile: UserProfile): Observable<UserAccountView> {
        const tasks = [
            this._httpClient.put(this.apiUrl + 'api/admin/user/' + user.id, user)
            , this._httpClient.put(this.apiUrl + 'api/admin/user-profile/' + profile.userId, profile)
        ];

        return forkJoin(tasks)
            .pipe(
                switchMap(() => {
                    return this.getUser(user.id);
                })
            );
    }

    applyNotificationPreferencesToSidemarks(userId: string): Observable<object> {
        return this._httpClient.put(this.apiUrl + 'api/admin/user-profile/' + userId + '/apply-notification-preferences-to-sidemarks', {});
    }

    updateUserPassword(newPassword: string, userId: string): Observable<object> {
        return this._httpClient.put(this.apiUrl + 'api/admin/user/password/' + userId, { newPassword: newPassword })
    }

    saveUserRoles(roles: AspNetUserRole[], userId: string): Observable<UserAccountView> {
        const tasks = roles.map((role: AspNetUserRole) => {
            return this._httpClient.post(this.apiUrl + 'api/admin/user-roles', role)
        })

        return forkJoin(tasks)
            .pipe(
                switchMap(() => {
                    return this.getUser(userId);
                })
            );
    }

    deleteUserRoles(userRoleIds: string[], userId: string): Observable<UserAccountView> {
        const tasks = userRoleIds.map((role: string) => {
            return this._httpClient.delete(this.apiUrl + 'api/admin/user-roles/' + role, {});
        })

        return forkJoin(tasks)
            .pipe(
                switchMap(() => {
                    return this.getUser(userId);
                })
            );
    }

    getUserRoles(): Observable<AspNetRole[]> {
        return this._httpClient.get<PageResponse<AspNetRole>>(this.apiUrl + 'api/roles').pipe(
            map((data) => {
                const ret = data.items;
                return ret;
            })
        );
    }

    getUserSubscription(userId: string, websiteId?: number): Observable<SubscriptionDetailView> {
        userId = !userId ? this.authService?.userProfile?.userProfile?.userId : userId;
        let requestParams = {};
        if (websiteId) requestParams = { params: { websiteId } };
        return this._httpClient.get<PageResponse<SubscriptionDetailView>>(this.apiUrl + 'api/admin/subscription/user/' + userId + '/detail', requestParams).pipe(
            map((data) => {
                const ret = data.item;
                this._userSubscription.next(ret);
                return ret;
            })
        );
    }

    getUsers(request: UsersListRequest): Observable<PageResponse<AdminUserView>> {
        const usersRequest = this.normalizeUsersRequest(request);
        return this._httpClient.get(this.apiUrl + 'api/admin/view/users', { params: usersRequest.toParams() }).pipe(
            map((data) => {
                const ret = data as PageResponse<AdminUserView>;
                this._users.next(ret);
                return ret;
            })
        );
    }

    exportUsers(request: UsersListRequest, fileType: 'csv' | 'xlsx' = 'xlsx'): void {
        const exportRequest = this.normalizeUsersRequest(request, fileType);
        this.usersViewService.export(exportRequest);
    }

    getUserCards(request: PageRequest): Observable<PageResponse<UserCreditCard>> {
        const pageRequest = new PageRequest().deserialize(request ?? {});
        return this._httpClient.get<PageResponse<UserCreditCardRow & { type?: string }>>(this.apiUrl + 'api/credit-card', { params: pageRequest.toParams() }).pipe(
            switchMap((data) => {
                const userCardAPIResponse = data;
                
                return this.userSubscription$.pipe(
                    filter(userSubscription => userSubscription !== null && userSubscription !== undefined),
                    map((userSubscription) => {
                        const userSubscriptionDetail: ProjectView[] = [];
                        
                        if (userSubscription) {
                            userSubscription?.projects?.forEach(element => {
                                userSubscriptionDetail.push(element?.project);
                            });
                            
                            userCardAPIResponse?.items?.forEach(card => {
                                card['type'] = CardTypeLogos[card?.cardType];
                                const matchingTokens = userSubscriptionDetail.filter(item => item?.creditCardId === card?.id);
                                card['sideMark'] = matchingTokens.map(obj => obj?.name).join(', ');
                            });
                        }
                        
                        this._userCards.next(userCardAPIResponse);
                        return userCardAPIResponse;
                    })
                );
            })
        );
    }

    getProjectUsers(request: UsersListRequest): Observable<PageResponse<SubscriptionView>> {
        if (request.sort && request.sortDir) {
            request[`sort[${request.sort}]`] = request.sortDir;
        }
        if (request.query) {
            request['orLike[fullName]'] = request.query;
            request['orLike[email]'] = request.query;
            request['orLike[phone]'] = request.query;
        }
        if(request.find?.websiteId) request['find[websiteId]'] = request.find.websiteId;
        if(request.find?.deleted) request['find[deleted]'] = request.find.deleted;
        if(request.find?.ownerUserId) request['find[ownerUserId]'] = request.find.ownerUserId;

        delete request?.find;
        delete request?.query;
        const params: Omit<UsersListRequest, 'find' | 'query'> = request;
        return this._httpClient.get(this.apiUrl + 'api/admin/view/subscription', { params }).pipe(
            map((data) => {
                const ret = data as PageResponse<SubscriptionView>;
                this._projectSubscription.next(ret);
                return ret;
            })
        );
    }

    deleteUser(userId: string): Observable<void> {
        return this._httpClient.delete<void>(this.apiUrl + 'api/admin/user/' + userId);
    }

    reactivateUser(userId: string): Observable<void> {
        return this._httpClient.put<void>(`${this.apiUrl}api/admin/user/reactivate/${userId}`, { userId });
    }

    generateJWTToken(id: string): Observable<string> {
        return this._httpClient.post(`${this.apiUrl}api/admin/user/${id}/generate-token`, {id}).pipe(
            map((data: PageResponse<{token: string}>) => data?.item?.token)
        );
    }

    getSalesRepresentatives(): Observable<AdminUserView[]> {
        const request = {
            itemsPerPage: 1000,
            currentPage: 1,
            'find[roleName]': Role.Sales,
            'find[deleted]': 'IS NULL'
        };
        return this._httpClient.get(this.apiUrl + 'api/admin/view/users', { params: request }).pipe(
            map((data) => {
                const ret = data as PageResponse<AdminUserView>;
                return ret.items || [];
            })
        );
    }

    private normalizeUsersRequest(request: UsersListRequest, fileType?: 'csv' | 'xlsx'): PageRequest {
        const usersRequest = new PageRequest();
        usersRequest.currentPage = Number(request?.currentPage) || 1;
        usersRequest.itemsPerPage = Number(request?.itemsPerPage) || environment.defaultItemsPerPage;
        usersRequest.sort = request?.sort;
        usersRequest.sortDir = request?.sortDir;
        usersRequest.fileType = fileType;

        const find = { ...(request?.find || {}) };

        if (request?.query) {
            usersRequest.orLike['fullName'] = request.query;
            usersRequest.orLike['email'] = request.query;
            usersRequest.orLike['phoneNumber'] = request.query;
        }

        if (find.tags) usersRequest.find['tags:split'] = find.tags;
        if (find.websiteNames) usersRequest.find['websiteNames:split'] = find.websiteNames;
        if (find.deleted) usersRequest.find['deleted'] = find.deleted;
        if (find.subscriptions) usersRequest.find['subscriptions'] = find.subscriptions;
        if (find.roleName) usersRequest.find['roleName'] = find.roleName;
        if (find?.['roleName:in']) usersRequest.find['roleName:in'] = request.find['roleName:in'];
        if (find.currentUser) usersRequest.find['userId:nin'] = find.currentUser;

        return usersRequest;
    }
}

@Injectable({
    providedIn: 'root',
})
export class AdminTagService extends CoreApiService<Tag> {
    protected endpoint = 'admin/tag';
    constructor() {
        super();
    }
}

@Injectable({
    providedIn: 'root',
})
export class AdminUserTagService extends CoreApiService<UserTag> {
    protected endpoint = 'admin/user-tag';
    constructor() {
        super();
    }

    setBulkCustomerTag(request: BulkRequest<UserTag>): Observable<UserTag[]> {
        return this.httpService.post<PageResponse<UserTag>>(`${this.baseUrl}api/${this.endpoint}/bulk`, request)
            .pipe(map((res) => res?.items));
    }
}

@Injectable({
    providedIn: 'root',
})
export class AdminUserPermissionService extends ReadOnlyCoreApiService<string> {
    constructor() {
        super('admin/user-permissions');
    }

    addUserPermission(userId: string, permission: Permission): Observable<string[]> {
        return this.httpService.put<string[]>(`${this.baseUrl}${this.serviceUrl}/${userId}/${permission}`, null);
    }

    removeUserPermission(userId: string, permission: Permission): Observable<string[]> {
        return this.httpService.delete<string[]>(`${this.baseUrl}${this.serviceUrl}/${userId}/${permission}`);
    }
}


